import { call, put, takeLatest } from "redux-saga/effects";
import apiService from "services/apiservice";
import { getErrorMessageString } from "utils/utils";
import { notificationController } from "components/common/notificationController";
import { loadState } from "library/helpers/sessionStorage";
import {
  addUpdateInvoiceMLEntryFailure,
  addUpdateInvoiceMLEntrySuccess,
  downloadInvoiceFileFailure,
  downloadInvoiceFileSuccess,
  fetchInvoiceDetailsByIDFailure,
  fetchInvoiceDetailsByIDSuccess,
  fetchInvoiceListFailure,
  fetchInvoiceListSuccess,
} from "./actions";
import {
  FETCH_INVOICE_LIST_REQUEST,
  DOWNLOAD_INVOICE_FILE_REQUEST,
  GET_INVOICE_DETAILS_BY_ID_REQUEST,
  ADD_UPDATE_INVOICE_ML_ENTRY_REQUEST,
} from "./actionTypes";

const currentUser = loadState("loggedInUser");
const token = currentUser?.accessToken;

const config = {
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  },
};

const getRequest = async (url: string) => {
  const { data } = await apiService.get(url, config);
  return data;
};

const getRequestForDownload = async (url: string, configVal: any) => {
  const data = await apiService.get(url, configVal);
  return data;
};

const postRequest = async (url: string, payload: any) => {
  const { data } = await apiService.post(url, payload, config);
  return data;
};

function* fetchInvoiceListSaga(action: any) {
  try {
    const response: { status: string } = yield call(
      postRequest,
      action.url,
      action.payload
    );

    yield put(fetchInvoiceListSuccess(response));
  } catch (error: any) {
    const errorMessage = getErrorMessageString(error);
    if (errorMessage && error?.response?.status !== 401) {
      notificationController.error({
        message: "Error",
        description: `Error fetching activity list: ${errorMessage}`,
      });
    }
    yield put(fetchInvoiceListFailure(errorMessage));
  }
}

function* downloadInvoiceFileSaga(action: any) {
  try {
    let fileName = action.payload.fileName;
    const response: { data: any; headers: any } = yield call(
      getRequestForDownload,
      action.url,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      }
    );
    const blob = new Blob([response.data], {
      type: response.headers["content-type"],
    });

    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName.substring(fileName.lastIndexOf("/") + 1);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    yield put(downloadInvoiceFileSuccess(response));
    action.callback("Success", "", {
      ...response,
      fileName: action?.payload?.fileName,
    });
  } catch (error: any) {
    const errorMessage = getErrorMessageString(error);
    yield put(downloadInvoiceFileFailure(errorMessage));
    action.callback("Error", errorMessage);
  }
}

function* fetchInvoiceDetailsByIDSaga(action: any) {
  try {
    const response: { status: string; message: string } = yield call(
      getRequest,
      action.url
    );
    yield put(fetchInvoiceDetailsByIDSuccess(response));
    action.callback("Success", response.message);
  } catch (error: any) {
    const errorMessage = getErrorMessageString(error);
    yield put(fetchInvoiceDetailsByIDFailure(errorMessage));
    action.callback("Error", errorMessage);
  }
}

function* addUpdateInvoiceMLEntrySaga(action: any) {
  try {
    const response: { status: string; message: string } = yield call(
      postRequest,
      action.url,
      action.payload
    );
    yield put(addUpdateInvoiceMLEntrySuccess(response.message));
    action.callback("Success", response.message);
  } catch (error: any) {
    const errorMessage = getErrorMessageString(error);
    yield put(addUpdateInvoiceMLEntryFailure(errorMessage));
    action.callback("Error", errorMessage);
  }
}

export default function* invoicesSagas() {
  yield takeLatest(FETCH_INVOICE_LIST_REQUEST, fetchInvoiceListSaga);
  yield takeLatest(DOWNLOAD_INVOICE_FILE_REQUEST, downloadInvoiceFileSaga);
  yield takeLatest(
    GET_INVOICE_DETAILS_BY_ID_REQUEST,
    fetchInvoiceDetailsByIDSaga
  );
  yield takeLatest(
    ADD_UPDATE_INVOICE_ML_ENTRY_REQUEST,
    addUpdateInvoiceMLEntrySaga
  );
}
